import { Button, Input } from "reactstrap";
import { SongUb } from "../../gql/graphql";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencilAlt,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { ItemCard } from "./ItemCard";
import { graphql } from "../../gql";
import { useMutation } from "urql";
import "./songFragments";
import { useState } from "react";
import { AddSongnumberModal } from "./AddSongnumberModal";
import { useSongbooksQuery } from "./useSongbooksQuery";
import { CyItem } from "./CyItem";
import { useTranslation } from "react-i18next";
import { useApiTranslation } from "../../i18n/useApiTranslation";

interface Props {
  song: SongUb;
  isDisabled?: boolean;
}

const updateSongnumbersMutation = graphql(`
  mutation updateSongnumbers(
    $bucket: String!
    $id: ID!
    $songnumbers: [SongnumberParameter!]!
  ) {
    updateSongnumbers(bucket: $bucket, id: $id, songnumbers: $songnumbers) {
      ...SongFields
    }
  }
`);

export function SongnumbersCard({ song, isDisabled = false }: Props) {
  const { t } = useTranslation();
  const { translateError } = useApiTranslation();
  const [, executeMutation] = useMutation(updateSongnumbersMutation);
  const { songbooks } = useSongbooksQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [editMode, setEditMode] = useState<
    { songbookId: string; number: string } | undefined
  >(undefined);

  const handleDeleteSongnumber = (songbookId: string, songbookName: string) => {
    const confirmed = window.confirm(
      t("items.songnumbers.deleteConfirm", { songbookName }),
    );

    if (!confirmed) {
      return;
    }

    // Filter out the songnumber with the given songbookId
    const updatedSongnumbers = song.songnumbers
      .filter((sn) => sn.songbook.id !== songbookId)
      .map((sn) => ({
        songbookId: sn.songbook.id,
        number: sn.number,
      }));

    executeMutation({
      bucket: song.bucket,
      id: song.id,
      songnumbers: updatedSongnumbers,
    }).then((result) => {
      console.debug("Mutation result", result);
      if (result.error) {
        alert(
          `${t("items.songnumbers.deleteError")}: ${translateError(result.error.message, result.error.message)}`,
        );
      }
    });
  };

  const handleEditSongnumber = (songbookId: string, currentNumber: string) => {
    setEditMode({ songbookId, number: currentNumber });
    setErrorMessage(undefined);
    setIsModalOpen(true);
  };

  const handleModalConfirm = (songbookId: string, number: string) => {
    // Clear previous error
    setErrorMessage(undefined);

    // Filter out the songbook entry (if exists) and add the new/updated one
    const updatedSongnumbers = [
      ...song.songnumbers
        .filter((sn) => sn.songbook.id !== songbookId)
        .map((sn) => ({
          songbookId: sn.songbook.id,
          number: sn.number,
        })),
      {
        songbookId,
        number,
      },
    ];

    executeMutation({
      bucket: song.bucket,
      id: song.id,
      songnumbers: updatedSongnumbers,
    }).then((result) => {
      console.debug("Mutation result", result);
      if (result.error) {
        setErrorMessage(
          `${t("items.songnumbers.addError")}: ${translateError(result.error.message, result.error.message)}`,
        );
      } else {
        setIsModalOpen(false);
        setEditMode(undefined);
      }
    });
  };

  const handleModalCancel = () => {
    setErrorMessage(undefined);
    setEditMode(undefined);
    setIsModalOpen(false);
  };

  const handleModalOpen = () => {
    setErrorMessage(undefined);
    setEditMode(undefined);
    setIsModalOpen(true);
  };

  // Filter out songbooks that already have a number assigned
  // Only filter when adding (not editing)
  const availableSongbooks = (
    editMode
      ? songbooks
      : songbooks.filter(
          (sb) => !song.songnumbers.some((sn) => sn.songbook.id === sb.id),
        )
  ).filter((sb) => sb.bucket === song.bucket);

  return (
    <ItemCard
      title={t("items.songnumbers.title")}
      helpCollapsedArea={
        <>
          <p>{t("items.songnumbers.helpText")}</p>
        </>
      }
    >
      <AddSongnumberModal
        isOpen={isModalOpen}
        onCancel={handleModalCancel}
        onConfirm={handleModalConfirm}
        availableSongbooks={availableSongbooks}
        errorMessage={errorMessage}
        editMode={editMode}
        bucket={song.bucket}
      />

      <Button
        color="primary"
        className="mb-3"
        disabled={isDisabled}
        onClick={handleModalOpen}
        data-cy={CyItem.AddSongnumberButton}
      >
        <FontAwesomeIcon icon={faPlus} className="me-2" />
        {t("items.songnumbers.addButton")}
      </Button>

      {song.songnumbers
        .slice()
        .sort((a, b) => a.songbook.mnemonic.localeCompare(b.songbook.mnemonic))
        .map((songnumber) => {
          return (
            <div
              key={songnumber.songbook.id}
              data-cy={CyItem.SongnumberDisplay}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{songnumber.songbook.name}</strong>
                  <br />
                  <small className="text-muted">
                    ({songnumber.songbook.mnemonic})
                  </small>
                </div>
                <div className="d-flex align-items-center">
                  <Input
                    value={songnumber.mnemonicNumber}
                    disabled={true}
                    className="form-control-readonly me-2"
                    style={{ width: "150px" }}
                  />
                  <Button
                    color="primary"
                    className="me-2"
                    disabled={isDisabled}
                    onClick={() =>
                      handleEditSongnumber(
                        songnumber.songbook.id,
                        songnumber.number,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </Button>
                  <Button
                    color="primary"
                    disabled={isDisabled}
                    onClick={() =>
                      handleDeleteSongnumber(
                        songnumber.songbook.id,
                        songnumber.songbook.name,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
    </ItemCard>
  );
}
